/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiAvatar,
  EuiButtonIcon,
  EuiButtonIconProps,
  EuiLink,
  EuiPopover,
  EuiPopoverProps,
  useEuiTheme,
} from "@inscopix/ideas-eui";
import { Tooltip } from "components/Tooltip/Tooltip";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import moment from "moment";
import { useState } from "react";
import { formatDate } from "utils/formatDate";

const AuthorInscopix = () => {
  return (
    <EuiAvatar
      size="s"
      name="Inscopix"
      iconType="users"
      color="#eee"
      type="space"
    />
  );
};

export interface ButtonToolInfoProps
  extends
    Pick<EuiButtonIconProps, "iconSize" | "css" | "color">,
    Pick<EuiPopoverProps, "anchorPosition" | "repositionOnScroll"> {
  dateCreated: string;
  dateUpdated: string;
  docsLink: string | null;
  codeLink: string | null;
  user: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
}

export const ButtonToolInfo = ({
  user,
  dateCreated,
  dateUpdated,
  docsLink,
  codeLink,
  anchorPosition,
  repositionOnScroll,
  ...props
}: ButtonToolInfoProps) => {
  const fullName = user ? `${user.first_name} ${user.last_name}` : "";
  const [isOpen, setIsOpen] = useState(false);
  const { euiTheme } = useEuiTheme();
  const styles = {
    dl: css`
      display: grid;
      grid-template-columns: max-content auto;
    `,
    dt: css`
      grid-column-start: 1;
      margin-right: 0.5rem;
      font-weight: bold;
      padding: 5px 0px;
    `,
    dtVertical: css`
      font-weight: bold;
      padding: 5px 0px 0px 0px;
    `,
    dd: css`
      padding: 5px 0px;
    `,
    ddVertical: css`
      padding: 0px 0px 5px 0px;
    `,
    subContent: css`
      color: ${euiTheme.colors.subduedText};
    `,
  };

  return (
    <Tooltip content="View Tool Info">
      <EuiPopover
        title=""
        button={
          <EuiButtonIcon
            aria-label="Project info"
            iconType="iInCircle"
            color="primary"
            iconSize="l"
            {...props}
          />
        }
        repositionOnScroll={repositionOnScroll}
        isOpen={isOpen}
        closePopover={() => setIsOpen(false)}
        onClick={() => setIsOpen((isOpen) => !isOpen)}
        anchorPosition={anchorPosition ?? "upLeft"}
      >
        <dl css={styles.dl}>
          <dt css={styles.dt}>Author</dt>
          <dd css={styles.dd}>
            {user ? (
              <UserAvatar userId={user.id} size="s" />
            ) : (
              <AuthorInscopix />
            )}
            <br />
            <span css={styles.subContent}>{user ? fullName : "Inscopix"}</span>
          </dd>

          <dt css={styles.dt}>Created</dt>
          <dd css={styles.dd}>
            {moment(dateCreated).fromNow()}
            <br />
            <span css={styles.subContent}>{formatDate(dateCreated)}</span>
          </dd>

          <dt css={styles.dt}>Updated</dt>
          <dd css={styles.dd}>
            {moment(dateUpdated).fromNow()}
            <br />
            <span css={styles.subContent}>{formatDate(dateUpdated)}</span>
          </dd>
        </dl>

        {docsLink !== null && docsLink !== "" && (
          <>
            <EuiLink external target="_blank" href={docsLink}>
              Documentation
            </EuiLink>
            <br />
          </>
        )}

        {codeLink !== null && codeLink !== "" && (
          <EuiLink external target="_blank" href={codeLink}>
            Source Code
          </EuiLink>
        )}
      </EuiPopover>
    </Tooltip>
  );
};
